import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../lib/format';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import {
  Truck,
  DollarSign,
  Save,
  AlertCircle,
  CheckCircle,
  Building2,
  User,
  RefreshCw,
  Edit2,
  X,
  Stethoscope,
  Search
} from 'lucide-react';

interface GlobalSettings {
  defaultShippingRate: number;
  bulkOrderShippingRate: number;
}

interface PhysicianShippingRate {
  id: string;
  email: string;
  name: string;
  clinicName: string;
  accountManager: {
    id: string;
    name: string;
    companyName: string;
  } | null;
  shippingRate?: number;
  bulkShippingRate?: number;
  effectiveShippingRate: number;
  effectiveBulkShippingRate: number;
  shippingSource: string;
  bulkShippingSource: string;
  hasShippingOverride: boolean;
  hasBulkShippingOverride: boolean;
}

interface AMShippingRate {
  id: string;
  email: string;
  name: string;
  companyName: string;
  shippingRate?: number;
  bulkShippingRate?: number;
  effectiveShippingRate: number;
  effectiveBulkShippingRate: number;
  hasShippingOverride: boolean;
  hasBulkShippingOverride: boolean;
}


export const AdminShippingSettings: React.FC = () => {
  const user = useUser();

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Global settings
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings>({
    defaultShippingRate: 0,
    bulkOrderShippingRate: 0
  });
  const [editedGlobalSettings, setEditedGlobalSettings] = useState<GlobalSettings>({
    defaultShippingRate: 0,
    bulkOrderShippingRate: 0
  });

  // AM shipping rates
  const [amRates, setAmRates] = useState<AMShippingRate[]>([]);
  const [editingAM, setEditingAM] = useState<string | null>(null);
  const [editedAMRates, setEditedAMRates] = useState<{
    shippingRate: string;
    bulkShippingRate: string;
  }>({ shippingRate: '', bulkShippingRate: '' });

  // Physician shipping rates
  const [physicianRates, setPhysicianRates] = useState<PhysicianShippingRate[]>([]);
  const [editingPhysician, setEditingPhysician] = useState<string | null>(null);
  const [editedPhysicianRates, setEditedPhysicianRates] = useState<{
    shippingRate: string;
    bulkShippingRate: string;
  }>({ shippingRate: '', bulkShippingRate: '' });
  const [physicianSearch, setPhysicianSearch] = useState('');

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch global settings, AM rates, and physician rates in parallel
      const [globalResponse, amResponse, physicianResponse] = await Promise.all([
        apiClient.getAdminShippingSettings(),
        apiClient.getAllAMShippingRates(),
        apiClient.getAllPhysicianShippingRates()
      ]);

      if (globalResponse.success && globalResponse.data) {
        setGlobalSettings(globalResponse.data);
        setEditedGlobalSettings(globalResponse.data);
      } else {
        setError(globalResponse.error || 'Failed to load global shipping settings');
      }

      if (amResponse.success && amResponse.data) {
        setAmRates(amResponse.data.accountManagers);
      } else {
        setError(amResponse.error || 'Failed to load AM shipping rates');
      }

      if (physicianResponse.success && physicianResponse.data) {
        setPhysicianRates(physicianResponse.data.physicians);
      } else {
        setError(physicianResponse.error || 'Failed to load physician shipping rates');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGlobalSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await apiClient.updateAdminShippingSettings({
        defaultShippingRate: editedGlobalSettings.defaultShippingRate,
        bulkOrderShippingRate: editedGlobalSettings.bulkOrderShippingRate
      });

      if (response.success && response.data) {
        setGlobalSettings(response.data);
        setEditedGlobalSettings(response.data);
        setSuccess('Global shipping settings updated successfully');

        // Reload AM and physician rates to reflect new effective rates
        const [amResponse, physicianResponse] = await Promise.all([
          apiClient.getAllAMShippingRates(),
          apiClient.getAllPhysicianShippingRates()
        ]);
        if (amResponse.success && amResponse.data) {
          setAmRates(amResponse.data.accountManagers);
        }
        if (physicianResponse.success && physicianResponse.data) {
          setPhysicianRates(physicianResponse.data.physicians);
        }
      } else {
        setError(response.error || 'Failed to update settings');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleEditAM = (am: AMShippingRate) => {
    setEditingAM(am.id);
    setEditedAMRates({
      shippingRate: am.shippingRate?.toString() || '',
      bulkShippingRate: am.bulkShippingRate?.toString() || ''
    });
  };

  const handleCancelEditAM = () => {
    setEditingAM(null);
    setEditedAMRates({ shippingRate: '', bulkShippingRate: '' });
  };

  const handleSaveAMRates = async (amId: string) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Parse values - empty string means clear override (null)
      const shippingRate = editedAMRates.shippingRate === ''
        ? null
        : parseFloat(editedAMRates.shippingRate);
      const bulkShippingRate = editedAMRates.bulkShippingRate === ''
        ? null
        : parseFloat(editedAMRates.bulkShippingRate);

      // Validate parsed values
      if (shippingRate !== null && (isNaN(shippingRate) || shippingRate < 0)) {
        setError('Individual order shipping rate must be a non-negative number or empty to use default');
        setSaving(false);
        return;
      }
      if (bulkShippingRate !== null && (isNaN(bulkShippingRate) || bulkShippingRate < 0)) {
        setError('Bulk order shipping rate must be a non-negative number or empty to use default');
        setSaving(false);
        return;
      }

      const response = await apiClient.updateAMShippingRates(amId, {
        shippingRate,
        bulkShippingRate
      });

      if (response.success) {
        setSuccess('AM shipping rates updated successfully');
        setEditingAM(null);

        // Reload AM rates
        const amResponse = await apiClient.getAllAMShippingRates();
        if (amResponse.success && amResponse.data) {
          setAmRates(amResponse.data.accountManagers);
        }
      } else {
        setError(response.error || 'Failed to update AM rates');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save AM rates');
    } finally {
      setSaving(false);
    }
  };

  const handleEditPhysician = (physician: PhysicianShippingRate) => {
    setEditingPhysician(physician.id);
    setEditedPhysicianRates({
      shippingRate: physician.shippingRate?.toString() || '',
      bulkShippingRate: physician.bulkShippingRate?.toString() || ''
    });
  };

  const handleCancelEditPhysician = () => {
    setEditingPhysician(null);
    setEditedPhysicianRates({ shippingRate: '', bulkShippingRate: '' });
  };

  const handleSavePhysicianRates = async (physicianId: string) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const shippingRate = editedPhysicianRates.shippingRate === ''
        ? null
        : parseFloat(editedPhysicianRates.shippingRate);
      const bulkShippingRate = editedPhysicianRates.bulkShippingRate === ''
        ? null
        : parseFloat(editedPhysicianRates.bulkShippingRate);

      if (shippingRate !== null && (isNaN(shippingRate) || shippingRate < 0)) {
        setError('Individual order shipping rate must be a non-negative number or empty to use default');
        setSaving(false);
        return;
      }
      if (bulkShippingRate !== null && (isNaN(bulkShippingRate) || bulkShippingRate < 0)) {
        setError('Bulk order shipping rate must be a non-negative number or empty to use default');
        setSaving(false);
        return;
      }

      const response = await apiClient.updatePhysicianShippingRates(physicianId, {
        shippingRate,
        bulkShippingRate
      });

      if (response.success) {
        setSuccess('Physician shipping rates updated successfully');
        setEditingPhysician(null);

        const physicianResponse = await apiClient.getAllPhysicianShippingRates();
        if (physicianResponse.success && physicianResponse.data) {
          setPhysicianRates(physicianResponse.data.physicians);
        }
      } else {
        setError(response.error || 'Failed to update physician rates');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save physician rates');
    } finally {
      setSaving(false);
    }
  };

  const filteredPhysicians = physicianRates.filter(p => {
    if (!physicianSearch) return true;
    const search = physicianSearch.toLowerCase();
    return (
      p.name.toLowerCase().includes(search) ||
      p.clinicName.toLowerCase().includes(search) ||
      p.email.toLowerCase().includes(search) ||
      p.accountManager?.name.toLowerCase().includes(search) ||
      p.accountManager?.companyName.toLowerCase().includes(search)
    );
  });

  const sourceLabel = (source: string) => {
    switch (source) {
      case 'physician_override': return 'Physician';
      case 'am_override': return 'AM';
      case 'system_default': return 'Global';
      default: return source;
    }
  };

  const sourceColor = (source: string) => {
    switch (source) {
      case 'physician_override': return 'text-teal-700 bg-teal-50';
      case 'am_override': return 'text-purple-700 bg-purple-50';
      case 'system_default': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Clear success/error messages after delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (!user || user.role !== 'admin') {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Admin access required</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Truck className="h-8 w-8 mr-3 text-blue-600" />
              Shipping Settings
            </h1>
            <p className="text-gray-600 mt-2">
              Configure default shipping rates, per-AM, and per-physician overrides
            </p>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading shipping settings...</span>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Global Settings Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                Global Default Rates
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                These rates apply to all orders unless overridden per AM
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Individual Order Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Individual Patient Order Rate
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      defaultValue={editedGlobalSettings.defaultShippingRate}
                      key={`default-rate-${editedGlobalSettings.defaultShippingRate}`}
                      onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) setEditedGlobalSettings({ ...editedGlobalSettings, defaultShippingRate: v }); }}
                      onBlur={(e) => { const v = parseFloat(e.target.value); const c = isNaN(v) || v < 0 ? 0 : v; setEditedGlobalSettings({ ...editedGlobalSettings, defaultShippingRate: c }); e.target.value = c.toFixed(2); }}
                      className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Applied to individual patient orders placed by physicians
                  </p>
                </div>

                {/* Bulk Order Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bulk Order Rate
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      defaultValue={editedGlobalSettings.bulkOrderShippingRate}
                      key={`bulk-rate-${editedGlobalSettings.bulkOrderShippingRate}`}
                      onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) setEditedGlobalSettings({ ...editedGlobalSettings, bulkOrderShippingRate: v }); }}
                      onBlur={(e) => { const v = parseFloat(e.target.value); const c = isNaN(v) || v < 0 ? 0 : v; setEditedGlobalSettings({ ...editedGlobalSettings, bulkOrderShippingRate: c }); e.target.value = c.toFixed(2); }}
                      className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Applied to bulk orders placed by AMs/physicians
                  </p>
                </div>
              </div>

              {/* Save Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSaveGlobalSettings}
                  disabled={saving || (
                    editedGlobalSettings.defaultShippingRate === globalSettings.defaultShippingRate &&
                    editedGlobalSettings.bulkOrderShippingRate === globalSettings.bulkOrderShippingRate
                  )}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Global Settings'}
                </button>
              </div>
            </div>
          </div>

          {/* Per-AM Overrides Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Building2 className="h-5 w-5 mr-2 text-purple-600" />
                Per-Account Manager Overrides
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Set custom shipping rates for specific Account Managers. Leave empty to use global defaults.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account Manager
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Individual Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bulk Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Effective Rates
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {amRates.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                        No Account Managers found
                      </td>
                    </tr>
                  ) : (
                    amRates.map((am) => (
                      <tr key={am.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="h-5 w-5 text-gray-400 mr-3" />
                            <div>
                              <div className="font-medium text-gray-900">{am.name}</div>
                              <div className="text-sm text-gray-500">
                                {am.companyName || am.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingAM === am.id ? (
                            <div className="relative w-24">
                              <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Default"
                                value={editedAMRates.shippingRate}
                                onChange={(e) => setEditedAMRates({
                                  ...editedAMRates,
                                  shippingRate: e.target.value
                                })}
                                className="w-full pl-6 pr-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          ) : (
                            <span className={am.hasShippingOverride ? 'font-medium text-purple-700' : 'text-gray-400'}>
                              {am.hasShippingOverride ? formatCurrency(am.shippingRate!) : 'Default'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingAM === am.id ? (
                            <div className="relative w-24">
                              <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Default"
                                value={editedAMRates.bulkShippingRate}
                                onChange={(e) => setEditedAMRates({
                                  ...editedAMRates,
                                  bulkShippingRate: e.target.value
                                })}
                                className="w-full pl-6 pr-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          ) : (
                            <span className={am.hasBulkShippingOverride ? 'font-medium text-purple-700' : 'text-gray-400'}>
                              {am.hasBulkShippingOverride ? formatCurrency(am.bulkShippingRate!) : 'Default'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <span className="text-gray-700">
                              Individual: <span className="font-medium">{formatCurrency(am.effectiveShippingRate)}</span>
                            </span>
                            <br />
                            <span className="text-gray-700">
                              Bulk: <span className="font-medium">{formatCurrency(am.effectiveBulkShippingRate)}</span>
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {editingAM === am.id ? (
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={handleCancelEditAM}
                                className="p-1.5 text-gray-500 hover:text-gray-700 border border-gray-300 rounded"
                              >
                                <X className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleSaveAMRates(am.id)}
                                disabled={saving}
                                className="p-1.5 text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                              >
                                <Save className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleEditAM(am)}
                              className="p-1.5 text-gray-500 hover:text-blue-600 border border-gray-300 rounded hover:border-blue-300"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-Physician Overrides Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Stethoscope className="h-5 w-5 mr-2 text-teal-600" />
                Per-Physician Overrides
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Set custom shipping rates for specific physicians. Leave empty to use AM or global defaults.
              </p>
            </div>
            <div className="px-6 py-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by physician name, clinic, email, or AM..."
                  value={physicianSearch}
                  onChange={(e) => setPhysicianSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Physician
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Clinic / AM
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Individual Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bulk Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Effective Rates
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPhysicians.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                        {physicianSearch ? 'No physicians match your search' : 'No physicians found'}
                      </td>
                    </tr>
                  ) : (
                    filteredPhysicians.map((physician) => (
                      <tr key={physician.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Stethoscope className="h-5 w-5 text-gray-400 mr-3" />
                            <div>
                              <div className="font-medium text-gray-900">{physician.name}</div>
                              <div className="text-sm text-gray-500">{physician.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            {physician.clinicName && (
                              <div className="text-gray-700">{physician.clinicName}</div>
                            )}
                            {physician.accountManager ? (
                              <div className="text-gray-500">
                                AM: {physician.accountManager.name}
                                {physician.accountManager.companyName && ` (${physician.accountManager.companyName})`}
                              </div>
                            ) : (
                              <div className="text-gray-400 italic">No AM</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingPhysician === physician.id ? (
                            <div className="relative w-24">
                              <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Default"
                                value={editedPhysicianRates.shippingRate}
                                onChange={(e) => setEditedPhysicianRates({
                                  ...editedPhysicianRates,
                                  shippingRate: e.target.value
                                })}
                                className="w-full pl-6 pr-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500"
                              />
                            </div>
                          ) : (
                            <span className={physician.hasShippingOverride ? 'font-medium text-teal-700' : 'text-gray-400'}>
                              {physician.hasShippingOverride ? formatCurrency(physician.shippingRate!) : 'Default'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingPhysician === physician.id ? (
                            <div className="relative w-24">
                              <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Default"
                                value={editedPhysicianRates.bulkShippingRate}
                                onChange={(e) => setEditedPhysicianRates({
                                  ...editedPhysicianRates,
                                  bulkShippingRate: e.target.value
                                })}
                                className="w-full pl-6 pr-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-teal-500"
                              />
                            </div>
                          ) : (
                            <span className={physician.hasBulkShippingOverride ? 'font-medium text-teal-700' : 'text-gray-400'}>
                              {physician.hasBulkShippingOverride ? formatCurrency(physician.bulkShippingRate!) : 'Default'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm space-y-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-gray-700">
                                Individual: <span className="font-medium">{formatCurrency(physician.effectiveShippingRate)}</span>
                              </span>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${sourceColor(physician.shippingSource)}`}>
                                {sourceLabel(physician.shippingSource)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-gray-700">
                                Bulk: <span className="font-medium">{formatCurrency(physician.effectiveBulkShippingRate)}</span>
                              </span>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${sourceColor(physician.bulkShippingSource)}`}>
                                {sourceLabel(physician.bulkShippingSource)}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {editingPhysician === physician.id ? (
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={handleCancelEditPhysician}
                                className="p-1.5 text-gray-500 hover:text-gray-700 border border-gray-300 rounded"
                              >
                                <X className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleSavePhysicianRates(physician.id)}
                                disabled={saving}
                                className="p-1.5 text-white bg-teal-600 hover:bg-teal-700 rounded disabled:opacity-50"
                              >
                                <Save className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleEditPhysician(physician)}
                              className="p-1.5 text-gray-500 hover:text-teal-600 border border-gray-300 rounded hover:border-teal-300"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Info Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">How Shipping Rates Work</h3>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Shipping is added as a flat fee per order</li>
              <li>Shipping revenue goes 100% to the platform (not included in AM payouts)</li>
              <li>Individual order rates apply to patient orders placed by physicians</li>
              <li>Bulk order rates apply to bulk inventory orders</li>
              <li>Per-physician overrides take highest precedence, then per-AM, then global defaults</li>
              <li>Leave per-physician or per-AM fields empty to fall through to the next level</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShippingSettings;
